import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./api-client";

export function useCreateBooking() {
  return useMutation({
    mutationFn: (payload) => apiClient({ url: "booking/create", method: "POST", data: payload })
  });
}

export function useGetAllBookings() {
  return useMutation({
    mutationFn: ({ status, bookingType }) => {
      const params = new URLSearchParams();
      if (status) {
        params.append("status", status);
      }
      if (bookingType) {
        params.append("bookingType", bookingType);
      }
      return apiClient({
        url: `admin/booking/all?${params.toString()}`,
        method: "GET"
      });
    }
  });
}

export function useGetBookingDetails(bookingSlug) {
  return useQuery({
    queryKey: ["booking", bookingSlug],
    queryFn: () =>
      apiClient({
        url: `admin/booking/details/${bookingSlug}`,
        method: "GET"
      }),
    enabled: !!bookingSlug
  });
}

export function useApproveBooking() {
  return useMutation({
    mutationFn: ({ bookingSlug, payload }) =>
      apiClient({
        url: `admin/booking-approval/${bookingSlug}`,
        method: "PATCH",
        data: payload
      })
  });
}

export function useQuickBookingApproval() {
  return useMutation({
    mutationFn: ({ bookingSlug, payload }) =>
      apiClient({
        url: `booking/approval/${bookingSlug}`,
        method: "PATCH",
        data: payload
      })
  });
}

export function useInviteWorkers() {
  return useMutation({
    mutationFn: ({ bookingSlug, payload }) =>
      apiClient({
        url: `admin/booking/invite-worker/${bookingSlug}`,
        method: "PATCH",
        data: payload
      })
  });
}

export function useRemoveWorkers() {
  return useMutation({
    mutationFn: ({ bookingSlug, payload }) =>
      apiClient({
        url: `admin/booking/remove-worker/${bookingSlug}`,
        method: "PATCH",
        data: payload
      })
  });
}

export function useGetWorkerBookings() {
  return useMutation({
    mutationFn: ({ page, limit, filter, search }) => {
      const params = new URLSearchParams();
      if (page) {
        params.append("page", String(page));
      }
      if (limit) {
        params.append("limit", String(limit));
      }
      if (filter) {
        params.append("filter", filter);
      }
      if (search) {
        params.append("search", search);
      }
      return apiClient({
        url: `booking/worker/get/all?${params.toString()}`,
        method: "GET"
      });
    }
  });
}

export function useGetClientBookings() {
  return useMutation({
    mutationFn: ({ page, limit, status, search }) => {
      const params = new URLSearchParams();
      if (page) {
        params.append("page", String(page));
      }
      if (limit) {
        params.append("limit", String(limit));
      }
      if (status) {
        params.append("status", status);
      }
      if (search) {
        params.append("search", search);
      }

      const query = params.toString();
      return apiClient({
        url: `booking/client/get/all${query ? `?${query}` : ""}`,
        method: "GET"
      });
    }
  });
}

export function useGetClientBookingDetails(bookingSlug) {
  return useQuery({
    queryKey: ["client-booking-details", bookingSlug],
    queryFn: () =>
      apiClient({
        url: `booking/details/${bookingSlug}`,
        method: "GET"
      }),
    enabled: !!bookingSlug
  });
}

export function useGetClientBookingAttendance({
  bookingSlug,
  shiftIndex,
  date,
  filter = "all",
  page = 1,
  limit = 100,
  enabled = true,
}) {
  return useQuery({
    queryKey: [
      "client-booking-attendance",
      bookingSlug,
      shiftIndex,
      date,
      filter,
      page,
      limit,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("bookingSlug", bookingSlug);
      params.set("shiftIndex", String(shiftIndex));
      params.set("date", date);
      params.set("filter", filter);
      params.set("page", String(page));
      params.set("limit", String(limit));
      return apiClient({
        url: `worker/attendance?${params.toString()}`,
        method: "GET",
      });
    },
    enabled:
      enabled &&
      !!bookingSlug &&
      !!date &&
      shiftIndex != null &&
      !Number.isNaN(shiftIndex),
    structuralSharing: false,
  });
}

export function useGetWorkerBookingRequestDetails(requestSlug) {
  return useQuery({
    queryKey: ["worker-booking-request", requestSlug],
    queryFn: () =>
      apiClient({
        url: `booking/worker/request/details/${requestSlug}`,
        method: "GET"
      }),
    enabled: !!requestSlug,
    retry: false // Disable automatic retries on failure
  });
}

export function useApproveWorkerBookingRequest() {
  return useMutation({
    mutationFn: ({ requestSlug, payload }) =>
      apiClient({
        url: `booking/worker/approval/${requestSlug}`,
        method: "PATCH",
        data: payload
      })
  });
}

export function useSubmitAttendance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingRequestSlug, workerSlug, time, lat, lng }) =>
      apiClient({
        url: `booking/worker/attendance/${bookingRequestSlug}`,
        method: "POST",
        data: { workerSlug, time, lat, lng },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-booking-attendance"] });
    },
  });
}

export function useAttendanceNoShowDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingSlug, shiftIndex, date, mode }) =>
      apiClient({
        url: "worker/attendance/no-show-decision",
        method: "POST",
        data: { bookingSlug, date, shiftIndex, mode },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-booking-attendance"] });
    },
  });
}

export function useUpdateBooking() {
  return useMutation({
    mutationFn: ({ bookingSlug, payload }) =>
      apiClient({
        url: `booking/update/${bookingSlug}`,
        method: "PATCH",
        data: payload
      })
  });
}

export function useApplyForLeave() {
  return useMutation({
    mutationFn: ({ bookingRequestSlug, payload }) =>
      apiClient({
        url: `leave-requests/${bookingRequestSlug}`,
        method: "POST",
        data: payload
      })
  });
}

export function useGetWorkerLeaveRequests() {
  return useMutation({
    mutationFn: ({ type, page, limit }) => {
      const params = new URLSearchParams();
      if (type) params.append("type", type);
      if (page) params.append("page", String(page));
      if (limit) params.append("limit", String(limit));

      const query = params.toString();
      return apiClient({
        url: `leave-requests/all${query ? `?${query}` : ""}`,
        method: "GET"
      });
    }
  });
}

export function useSubmitWorkerLeaveRequest() {
  return useMutation({
    mutationFn: (formData) =>
      apiClient({
        url: "leave-requests",
        method: "POST",
        data: formData
      })
  });
}

export function useSubmitWorkerLeaveFitNote() {
  return useMutation({
    mutationFn: ({ slug, formData }) =>
      apiClient({
        url: `leave-requests/${slug}/fit-note`,
        method: "PATCH",
        data: formData
      })
  });
}

export function useGetAllLeaveRequests() {
  return useMutation({
    mutationFn: ({ page, limit, status, search, type, sicknessMarkType }) => {
      const params = new URLSearchParams();
      if (page) params.append("page", String(page));
      if (limit) params.append("limit", String(limit));
      if (search) params.append("search", search);
      if (status && status !== "all") params.append("status", status);
      if (type && type !== "all") params.append("type", type);
      if (sicknessMarkType && sicknessMarkType !== "all") {
        params.append("sicknessMarkType", sicknessMarkType);
      }

      const query = params.toString();
      return apiClient({
        url: `admin/leave-requests/all${query ? `?${query}` : ""}`,
        method: "GET"
      });
    }
  });
}

export function useLeaveRequestDecision() {
  return useMutation({
    mutationFn: ({ slug, payload }) =>
      apiClient({
        url: `admin/leave-requests/${slug}/decision`,
        method: "PATCH",
        data: payload
      })
  });
}

export function useCreateInvoice() {
  return useMutation({
    mutationFn: (bookingSlug) =>
      apiClient({
        url: `admin/booking/invoice/${bookingSlug}`,
        method: "POST"
      })
  });
}

export function useGenerateInvoice() {
  return useMutation({
    mutationFn: ({ bookingSlug, payload }) =>
      apiClient({
        url: `admin/booking/${bookingSlug}/generate-invoice`,
        method: "POST",
        data: payload
      })
  });
}

/**
 * Plain GET for suggested workers. Prefer this over useMutation().mutate() when firing several
 * shifts in parallel — a single mutation observer can fail to run per-request onSuccess for each call.
 */
export async function fetchClientSuggestedWorkers({ bookingSlug, shiftIndex, limit, ...rest }) {
  const params = { ...rest };
  if (limit != null) params.limit = limit;
  return apiClient({
    url: `booking/client/suggested-workers/${bookingSlug}/${shiftIndex}`,
    method: "GET",
    params
  });
}

export async function fetchClientSuggestedWorkersSession(sessionId) {
  return apiClient({
    url: `booking/client/suggested-workers-session/${sessionId}`,
    method: "GET"
  });
}

export async function fetchClientInviteListWorkersSession(sessionId) {
  return apiClient({
    url: `booking/client/invite-list-workers-session/${sessionId}`,
    method: "GET"
  });
}

/** GET suggested remaining workers (omit `limit` on first load; pass `limit` for load more). */
export async function fetchClientSuggestedRemainingWorkers({
  bookingSlug,
  shiftIndex,
  limit,
  ...rest
}) {
  const params = { ...rest };
  if (limit != null) params.limit = limit;
  return apiClient({
    url: `booking/client/suggested-remaining-workers/${bookingSlug}/${shiftIndex}`,
    method: "GET",
    params
  });
}

export async function fetchClientSuggestedRemainingWorkersSession(sessionId) {
  return apiClient({
    url: `booking/client/suggested-remaining-workers-session/${sessionId}`,
    method: "GET"
  });
}

/**
 * Replacement suggested workers (omit `limit` on first load; pass `limit` for load more).
 * Session restore for this flow uses `suggestedReplacementWorkerSessionId` from booking detail
 * with `fetchClientSuggestedRemainingWorkersSession` (same URL as remaining-workers session).
 */
export async function fetchClientSuggestedReplacementWorkers({
  bookingSlug,
  shiftIndex,
  limit,
  ...rest
}) {
  const params = { ...rest };
  if (limit != null) params.limit = limit;
  return apiClient({
    url: `booking/client/suggested-replacement-workers/${bookingSlug}/${shiftIndex}`,
    method: "GET",
    params
  });
}

/** GET booking-worker-requests — `filter`: previouslyRejected | invited | rejected | accepted | all */
export async function fetchBookingWorkerRequests({
  bookingSlug,
  filter,
  page = 1,
  limit = 10,
  ...rest
}) {
  const params = { page, limit, ...rest };
  if (filter) params.filter = filter;
  return apiClient({
    url: `booking/client/booking-worker-requests/${bookingSlug}`,
    method: "GET",
    params
  });
}

/** Paginated worker requests (accepted, invited, etc.) for client booking screens. */
export function useGetBookingWorkerRequestsPage() {
  return useMutation({
    mutationFn: ({ bookingSlug, filter = "accepted", page = 1, limit = 10, ...rest }) =>
      fetchBookingWorkerRequests({
        bookingSlug,
        filter,
        page,
        limit,
        ...rest
      })
  });
}

/**
 * PATCH admin/booking/remove-worker/:bookingSlug — invalidates client booking + worker request lists.
 * Payload shape used elsewhere: `{ shifts: [{ shiftIndex, changes: [{ workerSlug, dates }] }] }`.
 */
export function useRemoveBookingWorkers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingSlug, payload }) =>
      apiClient({
        url: `admin/booking/remove-worker/${bookingSlug}`,
        method: "PATCH",
        data: payload
      }),
    onSuccess: (_, { bookingSlug }) => {
      if (!bookingSlug) return;
      queryClient.invalidateQueries({ queryKey: ["client-booking-details", bookingSlug] });
      queryClient.invalidateQueries({ queryKey: ["booking-worker-requests", bookingSlug] });
    }
  });
}

export function useCancelBookingKpi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingSlug, payload }) =>
      apiClient({
        url: `booking/client/cancel-booking-kpi/${bookingSlug}`,
        method: "PATCH",
        data: payload
      }),
    onSuccess: (_, { bookingSlug }) => {
      if (!bookingSlug) return;
      queryClient.invalidateQueries({ queryKey: ["client-booking-details", bookingSlug] });
      queryClient.invalidateQueries({ queryKey: ["booking-worker-requests", bookingSlug] });
    }
  });
}

export function useClaimBookingReward() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingSlug, type }) =>
      apiClient({
        url: "booking/client/reward/claim",
        method: "POST",
        data: { bookingSlug, type }
      }),
    onSuccess: (_, { bookingSlug }) => {
      if (!bookingSlug) return;
      queryClient.invalidateQueries({ queryKey: ["client-booking-details", bookingSlug] });
    }
  });
}

/** GET booking/client/suggested-workers/:bookingSlug/:shiftIndex — omit `limit` on first fetch; pass `limit` for load more (session-backed pagination on the server) */
export function useGetClientSuggestedWorkers() {
  return useMutation({
    mutationFn: fetchClientSuggestedWorkers
  });
}

/** GET booking/client/suggested-workers-session/:sessionId — restore prior session once */
export function useGetClientSuggestedWorkersSession() {
  return useMutation({
    mutationFn: fetchClientSuggestedWorkersSession
  });
}

export function useRejectClientWorker() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      apiClient({
        url: `booking/client/reject-worker`,
        method: "PATCH",
        data: payload
      }),
    onSuccess: (_, variables) => {
      const slug = variables?.bookingSlug;
      if (slug) {
        queryClient.invalidateQueries({ queryKey: ["client-booking-details", slug] });
        queryClient.invalidateQueries({ queryKey: ["booking-worker-requests", slug] });
      }
    }
  });
}

export function useManualInviteWorkers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingSlug, payload = {} }) =>
      apiClient({
        url: `booking/client/manual-invite-workers/${bookingSlug}`,
        method: "POST",
        data: payload
      }),
    onSuccess: (_, { bookingSlug }) => {
      queryClient.invalidateQueries({ queryKey: ["client-booking-details", bookingSlug] });
      queryClient.invalidateQueries({ queryKey: ["booking-worker-requests", bookingSlug] });
    }
  });
}

/** POST per shift — replacement find-replacements flow only. */
export function useManualInviteReplacementWorkers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ bookingSlug, shiftIndices, payload = {} }) => {
      let lastResponse;
      for (const shiftIndex of shiftIndices) {
        lastResponse = await apiClient({
          url: `booking/client/manual-invite-replacement-workers/${bookingSlug}/${shiftIndex}`,
          method: "POST",
          data: payload
        });
      }
      return lastResponse;
    },
    onSuccess: (_, { bookingSlug }) => {
      if (!bookingSlug) return;
      queryClient.invalidateQueries({ queryKey: ["client-booking-details", bookingSlug] });
      queryClient.invalidateQueries({ queryKey: ["booking-worker-requests", bookingSlug] });
    }
  });
}

export function useRequestClientInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bookingSlug) =>
      apiClient({
        url: `booking/client/request-invoice/${bookingSlug}`,
        method: "POST",
        data: {}
      }),
    onSuccess: (_, bookingSlug) => {
      queryClient.invalidateQueries({ queryKey: ["client-booking-details", bookingSlug] });
    }
  });
}

export function useClientStripeBookingPayment() {
  return useMutation({
    mutationFn: ({ bookingSlug, paymentType, invoiceIds }) =>
      apiClient({
        url: "booking/client/payment",
        method: "POST",
        data: { bookingSlug, paymentType, invoiceIds }
      })
  });
}

export function useClientGoCardlessIbpBookingPayment() {
  return useMutation({
    mutationFn: ({ bookingSlug, paymentType, invoiceIds }) =>
      apiClient({
        url: "booking/client/payment/gocardless-ibp",
        method: "POST",
        data: { bookingSlug, paymentType, invoiceIds }
      })
  });
}

export function useInviteListAppend() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingSlug, workers, isReplacementInvite }) =>
      apiClient({
        url: `booking/client/invite-list-workers/append`,
        method: "POST",
        data: {
          bookingSlug,
          workers,
          ...(isReplacementInvite === true ? { isReplacementInvite: true } : {})
        }
      }),
    onSuccess: (_, { bookingSlug }) => {
      queryClient.invalidateQueries({ queryKey: ["client-booking-details", bookingSlug] });
    }
  });
}

export function useInviteListRemove() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingSlug, shiftIndex, workerSlug, isReplacementRemove }) =>
      apiClient({
        url: `booking/client/invite-list-workers/remove`,
        method: "POST",
        data: {
          bookingSlug,
          shiftIndex,
          workerSlug,
          ...(isReplacementRemove === true ? { isReplacementRemove: true } : {}),
        },
      }),
    onSuccess: (_, { bookingSlug }) => {
      queryClient.invalidateQueries({ queryKey: ["client-booking-details", bookingSlug] });
    }
  });
}

function resolveStartSmartMatchingArgs(arg) {
  if (typeof arg === "string") {
    return { bookingSlug: arg, isReplacementMode: false };
  }
  return {
    bookingSlug: arg?.bookingSlug,
    isReplacementMode: arg?.isReplacementMode === true,
  };
}

export function useStartClientSmartMatching() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (arg) => {
      const { bookingSlug, isReplacementMode } = resolveStartSmartMatchingArgs(arg);
      return apiClient({
        url: `booking/client/start-smart-matching/${bookingSlug}`,
        method: "POST",
        data: isReplacementMode ? { isReplacementMode: true } : {},
      });
    },
    onSuccess: (_, arg) => {
      const { bookingSlug } = resolveStartSmartMatchingArgs(arg);
      queryClient.invalidateQueries({ queryKey: ["client-booking-details", bookingSlug] });
    },
  });
}

export function useSubmitWorkerBookingAgreement() {
  return useMutation({
    mutationFn: ({ bookingRequestSlug, formData }) =>
      apiClient({
        url: `booking/worker/submit-booking-agreement/${bookingRequestSlug}`,
        method: "POST",
        data: formData
      })
  });
}
